import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { logger } from '@/lib/logger';

interface ImpersonatedOrg {
  id: string;
  name: string;
}

interface AdminDashboardContextType {
  impersonatedOrg: ImpersonatedOrg | null;
  setImpersonatedOrg: (org: ImpersonatedOrg | null) => void;
  isImpersonating: boolean;
  clearImpersonation: () => void;
  startImpersonation: (org: ImpersonatedOrg) => Promise<void>;
  endImpersonation: () => Promise<void>;
}

const AdminDashboardContext = createContext<AdminDashboardContextType | undefined>(undefined);

export function AdminDashboardProvider({ children }: { children: ReactNode }) {
  const [impersonatedOrg, setImpersonatedOrgState] = useState<ImpersonatedOrg | null>(null);
  const { user } = useAuth();

  const logImpersonation = async (org: ImpersonatedOrg, action: 'start' | 'end') => {
    if (!user) return;
    
    try {
      await supabase.from('impersonation_logs').insert({
        super_admin_id: user.id,
        organization_id: org.id,
        organization_name: org.name,
        action
      });
    } catch (error) {
      logger.error('Failed to log impersonation', error);
    }
  };

  const startImpersonation = useCallback(async (org: ImpersonatedOrg) => {
    await logImpersonation(org, 'start');
    setImpersonatedOrgState(org);
  }, [user]);

  const endImpersonation = useCallback(async () => {
    if (impersonatedOrg) {
      await logImpersonation(impersonatedOrg, 'end');
    }
    setImpersonatedOrgState(null);
  }, [impersonatedOrg, user]);

  const setImpersonatedOrg = useCallback((org: ImpersonatedOrg | null) => {
    if (org) {
      startImpersonation(org);
    } else {
      endImpersonation();
    }
  }, [startImpersonation, endImpersonation]);

  const clearImpersonation = useCallback(() => {
    endImpersonation();
  }, [endImpersonation]);

  return (
    <AdminDashboardContext.Provider 
      value={{ 
        impersonatedOrg, 
        setImpersonatedOrg,
        isImpersonating: !!impersonatedOrg,
        clearImpersonation,
        startImpersonation,
        endImpersonation
      }}
    >
      {children}
    </AdminDashboardContext.Provider>
  );
}

export function useAdminDashboardContext() {
  const context = useContext(AdminDashboardContext);
  if (!context) {
    throw new Error('useAdminDashboardContext must be used within an AdminDashboardProvider');
  }
  return context;
}
